import { useMemo, useState } from "react";
import AdminLayout from "@/components/feature/AdminLayout";
import LoadingState from "@/components/feature/LoadingState";
import { bookingStages, type Booking } from "@/mocks/admin/bookings";
import { useBookings } from "@/hooks/admin/useAdminData";
import { supabase } from "@/lib/supabase";
import BookingCard from "./components/BookingCard";
import BookingDrawer from "./components/BookingDrawer";
import AddBookingModal from "./components/AddBookingModal";
import AvailabilityModal from "./components/AvailabilityModal";

export default function BookingsPage() {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Booking | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [toast, setToast] = useState("");
  const { data: bookings, loading, error, refetch } = useBookings();

  const handleCreated = async (booking: Booking) => {
    await refetch();
    setToast(`Booking ${booking.id} created for ${booking.customer}`);
    setTimeout(() => setToast(""), 3500);
  };

  const handleDrawerUpdated = async () => {
    const currentId = selected?.id;
    const { data: rows } = await supabase
      .from("bookings")
      .select("*")
      .order("created_at", { ascending: false });
    await refetch();
    if (currentId && rows) {
      const updated = rows.find((r: { id: string }) => r.id === currentId);
      if (updated) {
        setSelected({
          ...selected!,
          stage: updated.stage,
          estimatedQuote:
            updated.estimated_quote === null ? null : Number(updated.estimated_quote),
          depositRequired:
            updated.deposit_required === null
              ? null
              : Number(updated.deposit_required),
          depositPaid: !!updated.deposit_paid,
        });
        const stageLabels: Record<string, string> = {
          quoted: "Quote sent",
          confirmed: "Deposit confirmed",
          scheduled: "Project scheduled",
        };
        if (stageLabels[updated.stage]) {
          setToast(`${stageLabels[updated.stage]} for ${selected!.customer}`);
          setTimeout(() => setToast(""), 3500);
        }
      } else {
        setSelected(null);
        setToast("Booking removed");
        setTimeout(() => setToast(""), 3000);
      }
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return bookings;
    const q = search.toLowerCase();
    return bookings.filter(
      (b) =>
        b.customer.toLowerCase().includes(q) ||
        b.id.toLowerCase().includes(q) ||
        b.fromCity.toLowerCase().includes(q) ||
        b.toCity.toLowerCase().includes(q) ||
        b.moveType.toLowerCase().includes(q),
    );
  }, [search, bookings]);

  const byStage = useMemo(() => {
    const map: Record<string, Booking[]> = { new: [], quoted: [], confirmed: [], scheduled: [] };
    filtered.forEach((b) => {
      if (map[b.stage]) map[b.stage].push(b);
    });
    return map;
  }, [filtered]);

  return (
    <AdminLayout>
      <div className="px-4 md:px-8 py-6 md:py-8 max-w-[1600px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs text-ink-500 mb-2">
              <span>Operations</span>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-ink-900 font-semibold">Bookings</span>
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-ink-900 leading-tight">
              Booking Pipeline
            </h1>
            <p className="text-sm text-ink-500 mt-1.5 max-w-xl">
              Every incoming move request from first inquiry to confirmed and scheduled. Drag-and-drop
              between stages or click any card to see details.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAvailabilityOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold border border-ink-200 bg-white text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-line"></i>
              Availability
            </button>
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold bg-brand-500 hover:bg-brand-600 text-white cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Add Booking
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white border border-ink-100 rounded-lg flex flex-col md:flex-row md:items-center gap-3 px-4 py-3">
          <div className="relative flex-1 min-w-0">
            <span className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search bookings by customer, ID, or city…"
              className="w-full text-sm pl-10 pr-3 py-2.5 rounded-md bg-cream-50 border border-ink-100 focus:bg-white focus:border-ink-300 outline-none transition-colors"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button className="inline-flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-semibold border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap">
              <i className="ri-filter-3-line"></i>
              Filters
            </button>
            <button className="inline-flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-semibold border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap">
              <i className="ri-sort-desc"></i>
              Sort
            </button>
          </div>
        </div>

        {/* Kanban */}
        {loading ? (
          <LoadingState message="Loading bookings…" />
        ) : error ? (
          <div className="bg-white border border-ink-100 rounded-lg py-20 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-red-50 text-red-400 mx-auto mb-3">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <div className="font-display text-lg font-bold text-ink-900">Failed to load bookings</div>
            <div className="text-sm text-ink-500 mt-1">{error}</div>
            <button
              onClick={refetch}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-brand-500 hover:bg-brand-600 text-white cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              Retry
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {bookingStages.map((stage) => {
              const items = byStage[stage.key] || [];
              const totalValue = items.reduce((s, b) => s + (b.estimatedQuote || 0), 0);
              return (
                <div
                  key={stage.key}
                  className={`bg-cream-50 border-t-2 ${stage.accent} rounded-lg overflow-hidden flex flex-col`}
                >
                  <div className="px-4 pt-3 pb-3 flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${stage.color}`}></span>
                        <h3 className="text-sm font-bold text-ink-900">{stage.label}</h3>
                        <span className="text-[10px] font-bold bg-white border border-ink-100 text-ink-700 px-1.5 py-0.5 rounded">
                          {items.length}
                        </span>
                      </div>
                      <div className="text-[10px] text-ink-500 mt-1">{stage.description}</div>
                    </div>
                    <button className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-white text-ink-400 cursor-pointer">
                      <i className="ri-add-line text-base"></i>
                    </button>
                  </div>

                  <div className="px-3 pb-3 text-[10px] text-ink-500 flex items-center justify-between border-b border-ink-100">
                    <span>Pipeline value</span>
                    <span className="font-bold text-ink-900 tabular-nums">
                      ${totalValue.toLocaleString()}
                    </span>
                  </div>

                  <div className="p-3 space-y-2.5 min-h-[200px]">
                    {items.length === 0 ? (
                      <div className="text-center text-xs text-ink-400 py-8">
                        <i className="ri-inbox-line text-2xl block mb-1"></i>
                        No bookings
                      </div>
                    ) : (
                      items.map((b) => (
                        <BookingCard key={b.id} booking={b} onClick={() => setSelected(b)} />
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <BookingDrawer
        booking={selected}
        onClose={() => setSelected(null)}
        onUpdated={handleDrawerUpdated}
      />

      <AddBookingModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreate={handleCreated}
      />

      <AvailabilityModal
        open={availabilityOpen}
        onClose={() => setAvailabilityOpen(false)}
        bookings={bookings}
      />

      <div
        className={`fixed bottom-6 right-6 z-[60] transition-all duration-300 ${
          toast ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"
        }`}
      >
        <div className="bg-ink-900 text-white text-sm font-semibold px-4 py-3 rounded-md border border-ink-800 flex items-center gap-2 max-w-sm">
          <span className="w-6 h-6 flex items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300">
            <i className="ri-check-line text-sm"></i>
          </span>
          {toast}
        </div>
      </div>
    </AdminLayout>
  );
}